import React, { useState, useContext, useEffect } from 'react';
import { FieldData, FieldOption, JobItemsFieldsetType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { JobItemType, createNewJobItem } from '../../../views/jobs/JobTypes';
import Dropdown from '../basefields/Dropdown';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { parseQuantityAndUnit } from 'services/utils/parseQuantityAndUnit';
import { v4 as uuidv4 } from 'uuid';
import { VatRateType } from '../../../views/settings/SettingsTypes';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import { formatPriceCurrencyString, formatPriceString } from 'services/utils/amountFormatting';
import { renderRowsFieldErrorMessages } from './functions/renderRowsFieldErrorMessages';
import SearchTextField from '../basefields/SearchTextField';

const JobItemsFieldset: React.FC<JobItemsFieldsetType & { data: FieldData }> = ({
    name, jobId, data, dropdownData, helperText, isEditable
}) => {
    const { t } = useTranslation();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const { errorMessages } = useGlobalContext();
    const { userLocale, defaultCurrency, defaultCurrencySymbol } = useSettings();
    const hasRightCheck = useAllowedRight;

    // Build the rows from the fetched data or create an empty row
    const buildRowsFromData = (): JobItemType[] => {
        if (data && data[name] && data[name].length > 0) {
            return (data[name] || []).sort((a: any, b: any) => a.position - b.position).map((row: any) => ({
                ...row,
                sale_price: row.sale_price !== null ? new Intl.NumberFormat(userLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2}).format(row.sale_price.toString()) : null,
            }));
        }
        const firstPosition = 1
        return [createNewJobItem(jobId!, uuidv4(), firstPosition)];
    };

    // Initialize the states directly to prevent an empty first paint of the field
    const [rows, setRows] = useState<JobItemType[]>(() => buildRowsFromData());
    const [vatRates, setVatRates] = useState<VatRateType[]>(dropdownData?.vatrate?.results || []);
    const [showPriceFields, setShowPriceFields] = useState<boolean>(() => !(data && data['billing_type'] === 'fixed'));
    const [userHasOnlyViewRights] = useState<boolean>(hasRightCheck('only_view'));

    // If there are items included in the fetched data, show them. Otherwise show an empty row
    useEffect(() => {
        setRows(buildRowsFromData());
    }, [data, jobId]);

    // Show the price fields based on the billing type of the job
    useEffect(() => {
        if (data && data['billing_type']) {
            // Get the billing type from the job data
            const billingType = data['billing_type'];

            // Determine the visibility of the price fields
            if (billingType === 'calculation' || billingType === 'non_billing') {
                setShowPriceFields(true);
            } else if (billingType === 'fixed') {
                setShowPriceFields(false);
            }
        }
    }, [data]);

    // Set the vat rates from the dropdown data
    useEffect(() => {
        setVatRates(dropdownData?.vatrate?.results || []);
    }, [dropdownData])

    // Map job item name to field option
    const jobItemNameToFieldOption = (row: JobItemType): FieldOption | null => {
        if (row.product !== null) {
            return {
                id: row.product,
                value: row.name,
                name: row.name
            };
        } else if (row.name) {
            return {
                value: row.name,
                name: row.name,
            };
        }
        return null;
    }

    // Determine if the job item name is a textual input
    const isTextualInput = (row: JobItemType): boolean => {
        // Check if the job item has a product id
        if (row.product === null && row.name) {
            return true;
        }
        return false
    }

    // Generic function to update the rows with a changed value
    const updateRows = (identifier: string, updateFunction: (item: JobItemType, ...args: any[]) => JobItemType, ...args: any[]) => {
        // Copy the current rows and find its index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);
      
        if (rowIndex !== -1) {
            // Set the new value in the specific row
            updatedRows[rowIndex] = updateFunction(updatedRows[rowIndex], ...args);
      
            // Update the rows and updated data with sale price as float
            setRows(updatedRows);
            updateUpdatedDataWithPricesAsFloat(updatedRows);
        }
    };

    // Parse the price input to the right format
    function parsePriceInput(value: string | number | null | undefined) {
        if (typeof value === 'string') {
            return value ? parseFloat(value.replace(',', '.')) : null;
        }

        if (typeof value === 'number') {
            return value;
        }

        return null;
    }

    // Helper function to update the updated data with prices converted to float
    function updateUpdatedDataWithPricesAsFloat(updatedJobItems: JobItemType[]) {
        const updatedJobItemsForUpdatedData = updatedJobItems.map(job_item => ({
            ...job_item,
            sale_price: parsePriceInput(job_item.sale_price),
            purchase_price: parsePriceInput(job_item.purchase_price),
        }));

        setUpdatedData((currentUpdatedData: any) => ({ 
            ...currentUpdatedData, 
            [name]: updatedJobItemsForUpdatedData 
        }));
    }

    // Handle the quantity change
    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the job items and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Parse the quantity and unit from the quantity field
            const { quantity, unit } = parseQuantityAndUnit(event.target.value)

            // Set the quantity and unit in the updated job items
            updatedRows[rowIndex].quantity_registrated_string = event.target.value;
            updatedRows[rowIndex].quantity_registrated = quantity;
            updatedRows[rowIndex].unit = unit;

            // Calculate the total price change, only if a sale price and quantity is set
            if (updatedRows[rowIndex].sale_price !== null && quantity !== null) {
                const salePrice = parseFloat((updatedRows[rowIndex].sale_price || "0").replace(',', '.'));
                updatedRows[rowIndex].total_price = quantity * salePrice;
            }
            
            // Update the job items and updated data with sale price as float
            setRows(updatedRows);
            updateUpdatedDataWithPricesAsFloat(updatedRows);
        }        
    };

    // Handle the sale price change
    const handleSalePriceChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the job items and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Keep the sale price input as string
            const salePriceInput = event.target.value;

            // Set the sale price in the updated job items
            updatedRows[rowIndex].sale_price = salePriceInput;

            // If sale price is set, calculate the total price
            if (salePriceInput !== null) {
                const salePrice = parseFloat(salePriceInput.replace(',', '.'));
                const quantity = updatedRows[rowIndex].quantity_registrated || 0;
                updatedRows[rowIndex].total_price = quantity * salePrice;
            }

            // Update the job items
            setRows(updatedRows);

            // Convert the sale price to a float for the backend
            setUpdatedData((currentUpdatedData: any) => {
                const copyOfUpdatedData = { ...currentUpdatedData };
    
                // If no updated data exist, create a new array
                if (!copyOfUpdatedData[name]) {
                    copyOfUpdatedData[name] = [];
                }
                
                // Find the row from the updated data to update
                const updatedIndex = copyOfUpdatedData[name].findIndex((item: any) => item.id?.toString() === identifier || item.uuid === identifier);
    
                // Set the sale price to a float in the updated data for the found row or add the value to the new row
                if (updatedIndex !== -1) {
                    copyOfUpdatedData[name][updatedIndex] = {
                        ...updatedRows[rowIndex],
                        sale_price: salePriceInput ? parseFloat(salePriceInput.replace(',', '.')) : null
                    };
                } else {
                    copyOfUpdatedData[name].push({
                        ...updatedRows[rowIndex],
                        sale_price: salePriceInput ? parseFloat(salePriceInput.replace(',', '.')) : null
                    });
                }
    
                return copyOfUpdatedData;
            });
        }
    };

    // Handle the purchase price change
    const handlePurchasePriceChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the job items and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Get the purchase price input value
            const purchasePriceInput = event.target.value;

            // Set the purchase price in the updated job items
            updatedRows[rowIndex].purchase_price = purchasePriceInput;

            // Parse the price value to the right format
            const purchasePrice = parsePriceInput(purchasePriceInput);
            
            // Calculate the total purchase price
            if (purchasePrice !== null) {
                const quantity = updatedRows[rowIndex].quantity_registrated || 0;
                updatedRows[rowIndex].total_purchase_price = quantity * purchasePrice;
            }

            // Update states
            setRows(updatedRows);
            updateUpdatedDataWithPricesAsFloat(updatedRows);
        }
    };

    // Handle the name change
    const handleNameChange = (selectedItem: any, identifier: string) => {
        // Copy the job items and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(item => item.id?.toString() === identifier || item.uuid === identifier);

        if (rowIndex !== -1) {
            let salePrice;
            let quantity = updatedRows[rowIndex].quantity_registrated || 0;

            // Check if the selectedItem is a product object or just a name
            if (selectedItem && selectedItem.id) {
                // Set the product id, name and sale price from the selected product
                updatedRows[rowIndex].product = selectedItem.id;
                updatedRows[rowIndex].name = selectedItem.name;
                updatedRows[rowIndex].number = (selectedItem.number !== null ? selectedItem.number : null)
                updatedRows[rowIndex].sale_price = (selectedItem.sale_price !== null ? selectedItem.sale_price.toString() : null);
                updatedRows[rowIndex].sale_price_currency = (selectedItem.sale_price_currency !== null ? selectedItem.sale_price_currency : null);
                updatedRows[rowIndex].purchase_price = selectedItem.purchase_price !== null ? selectedItem.purchase_price.toString() : null;
                updatedRows[rowIndex].purchase_price_currency = selectedItem.purchase_price_currency !== null ? selectedItem.purchase_price_currency : null;
                updatedRows[rowIndex].vat_rate = selectedItem.vat_rate;
                updatedRows[rowIndex].ledger_account = selectedItem.ledger_account;
                salePrice = selectedItem.sale_price;
            } else if (selectedItem && selectedItem.name && selectedItem.value) {
                // Set the name if just a string is entered
                updatedRows[rowIndex].name = selectedItem.name;
                if (updatedRows[rowIndex].sale_price !== null) {
                    const salePriceStr = updatedRows[rowIndex].sale_price;
                    if (salePriceStr !== null) {
                        salePrice = parseFloat(salePriceStr);
                    }
                } else {
                    salePrice = null;
                }
            }

            // Calculate the total price change if a quantity exists and sale price is set
            if (quantity > 0 && salePrice !== null) {
                updatedRows[rowIndex].total_price = (quantity * salePrice);
            }

            // Update the job items and updated data with sale price as float
            setRows(updatedRows);
            updateUpdatedDataWithPricesAsFloat(updatedRows);
        }
    }

    // Handle the vat rate change
    const handleVatRateChange = (selectedValue: string, identifier: string) => {
        updateRows(identifier, (item, newVatRate) => {
            // Set the new vat rate
            const updatedItem = {
                ...item,
                vat_rate: parseFloat(newVatRate)
            };
        
            return updatedItem;
        }, selectedValue);
    };
    
    // Handle the creation of a new row
    const handleAddRow = () => {
        // Split job items in non deleted and deleted items
        const nonDeletedItems = rows.filter(row => !row.deleted);
        const deletedItems = rows.filter(row => row.deleted);

        // Determine the position of the new item and create it
        const newPosition = nonDeletedItems.length + 1;
        const newJobItem = createNewJobItem(jobId!, uuidv4(), newPosition);
    
        // Add the new row at the end of the list with non deleted items and add the deleted items
        const updatedNonDeletedItems = [...nonDeletedItems, newJobItem];
        const updatedJobItems = [...updatedNonDeletedItems, ...deletedItems];
    
        // Update the job items and updated data with sale price as float
        setRows(updatedJobItems);
        updateUpdatedDataWithPricesAsFloat(updatedJobItems);
    };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        const updatedJobItems = [...rows];
        const itemIndex = updatedJobItems.findIndex(item => item.id?.toString() === identifier || item.uuid === identifier);

        if (itemIndex !== -1) {
            // Mark the item as deleted without actually deleting it
            updatedJobItems[itemIndex].deleted = true;
    
            // Update the ordering of the items which are not marked as deleted
            const nonDeletedItems = updatedJobItems.filter(item => !item.deleted);
            const reorderedNonDeletedItems = nonDeletedItems.map((item, index) => ({
                ...item,
                position: index + 1,
            }));

            // Add the deleted items to the reordered list to keep them for the updated data
            const finalItems = [...reorderedNonDeletedItems, ...updatedJobItems.filter(item => item.deleted)];
    
            setRows(finalItems);
            updateUpdatedDataWithPricesAsFloat(finalItems);
        } 
    }

    // Handle ordering change
    const handleOrderingChange = (result: any) => {
        if (!result.destination) return;
        const { source, destination } = result;
        
        // Only use the non deleted items to determine the new ordering
        const nonDeletedItems = rows.filter(row => !row.deleted);
        const [reorderedItem] = nonDeletedItems.splice(source.index, 1);
        nonDeletedItems.splice(destination.index, 0, reorderedItem);
    
        // Determine the ordering for the non deleted items
        const updatedNonDeletedItems = nonDeletedItems.map((item, index) => ({
            ...item,
            position: index + 1,
        }));
    
        // Add the deleted items to the list without changing their ordering
        const finalItems = [...updatedNonDeletedItems, ...rows.filter(row => row.deleted)];
    
        setRows(finalItems);
        updateUpdatedDataWithPricesAsFloat(finalItems);
    };

    // Format a price input value into a locale string for the view mode
    const formatPriceValue = (value: string | number | null | undefined): string => {
        // Parse the price value to a float
        const parsedPrice = parsePriceInput(value);

        // Return an empty string if no price is set
        if (parsedPrice === null || isNaN(parsedPrice)) {
            return '';
        }

        // Format the price into a locale string
        return new Intl.NumberFormat(userLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parsedPrice);
    }

    // Format the total price value
    const formatTotalPrice = (row: JobItemType, showFallback?: boolean): string => {
        if (row.total_price) {

            let currency;
            if (row.sale_price_currency) {
                // If a sale price currency is found, use the currency of the product
                currency = row.sale_price_currency.toUpperCase();
            } else {
                // Otherwise fallback to the default currency of the environment
                currency = defaultCurrency?.toUpperCase() || 'EUR';
            }

            // Format the total price
            const formattedTotalPrice = formatPriceCurrencyString(row.total_price.toString(), userLocale, currency);

            return formattedTotalPrice;

        } else if (showFallback) {
            // Show zero as a fallback
            const formattedZero = formatPriceString("0", userLocale);

            return `${defaultCurrencySymbol} ${formattedZero}`
        } else {
            return '';
        }
    }

    return (
        <div className='rows-field'>
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing ? (
                // Edit mode
                <div className='edit-mode'>
                    {rows.length > 0 && (
                        <div className={`rows-header job-items-row ${showPriceFields ? 'show-price-fields' : ''} `}>
                            <div className='header-item'>{t('job.items.quantity_label')}</div>
                            <div className='header-item'>{t('job.items.description_label')}</div>
                            <div className='header-item'>{t('job.items.purchase_price_label')}</div>
                            {showPriceFields && <>
                                <div className='header-item'>{t('job.items.price_label')}</div>
                                <div className='header-item'>{t('job.items.total_label')}</div>
                                <div className='header-item'>{t('job.items.vat_rate_label')}</div>
                            </>}
                            <div className='delete-placeholder'></div>
                            <div className='drag-placeholder'></div>
                        </div>
                    )}
                    <DragDropContext onDragEnd={handleOrderingChange}>
                        <StrictModeDroppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {rows && rows.filter(row => row.deleted === false).map((row, index) => {
                                        const identifier = row.id?.toString() || row.uuid;
                                        return (
                                            <Draggable key={identifier} draggableId={`item-${identifier}`} index={index} isDragDisabled={rows.length <= 1}>
                                                {(provided) => {
                                                    return (
                                                        <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                            <div className={`job-items-row ${showPriceFields ? 'show-price-fields' : ''} `}>
                                                                <input 
                                                                    type="text"
                                                                    id={`quantity_registrated_string_${identifier}`}
                                                                    name={`quantity_registrated_string_${identifier}`}
                                                                    value={row.quantity_registrated_string ?? ''}
                                                                    onChange={event => handleQuantityChange(event, identifier || '')}
                                                                    placeholder={t('job.items.quantity_placeholder')}
                                                                    autoFocus={false}
                                                                    className={identifier && errorMessages?.items?.[identifier]?.['quantity_registrated'] ? 'is-invalid' : undefined}
                                                                />
                                                                <SearchTextField
                                                                    key={identifier}
                                                                    type='searchtextfield' 
                                                                    name='name'
                                                                    value={jobItemNameToFieldOption(row)}
                                                                    onSelect={(selectedItem) => handleNameChange(selectedItem, identifier || '')}
                                                                    data={data}
                                                                    placeholder='job.items.article_description_placeholder'
                                                                    objectName='job.items.article_description_object_name'
                                                                    query={{ endpoint: 'get_product_list', params: { is_active: true }}}
                                                                    postEndpoint='post_product'
                                                                    disableAddNew={true}
                                                                    allowTextualInput={true}
                                                                    isTextualValue={isTextualInput(row)}
                                                                    optionFormat={{ 
                                                                        title: { field: 'name', format: (value) => value },
                                                                        bracket: { field: 'number', format: (value) => value, optional: true }, 
                                                                    }}
                                                                    selectionFormat='name'
                                                                    isInvalid={identifier && errorMessages?.items?.[identifier]?.['name'] ? true : false}
                                                                />
                                                                <input 
                                                                    type="text"
                                                                    id={`purchase_price_${row.id || row.uuid}`}
                                                                    name={`purchase_price_${row.id || row.uuid}`}
                                                                    value={row.purchase_price ?? undefined}
                                                                    onChange={event => handlePurchasePriceChange(event, row.id?.toString() || row.uuid || '')}
                                                                    placeholder={t('job.items.price_placeholder')}
                                                                    className={`price-field ${identifier && errorMessages?.items?.[identifier]?.['purchase_price'] ? 'is-invalid' : ''}`}
                                                                />
                                                                {showPriceFields && (
                                                                    <>
                                                                        <input 
                                                                            type="text"
                                                                            id={`sale_price_${row.id || row.uuid}`}
                                                                            name={`sale_price_${row.id || row.uuid}`}
                                                                            value={row.sale_price ?? undefined}
                                                                            onChange={event => handleSalePriceChange(event, row.id?.toString() || row.uuid || '')}
                                                                            placeholder={t('job.items.price_placeholder')}
                                                                            className={`price-field ${identifier && errorMessages?.items?.[identifier]?.['sale_price'] ? 'is-invalid' : ''}`}
                                                                        />
                                                                        <div className='string-value price-value'>
                                                                            {formatTotalPrice(row, true)}
                                                                        </div>
                                                                        <Dropdown<VatRateType>
                                                                            options={vatRates}
                                                                            id={`vat_rate_${identifier}`}
                                                                            name={`vat_rate_${identifier}`}
                                                                            disabled_selected={t('job.items.vat_rate_placeholder')}
                                                                            selectedOption={vatRates.find(vatRate => row.vat_rate === vatRate.id)}
                                                                            onChange={(selectedValue) => handleVatRateChange(selectedValue, row.id?.toString() || row.uuid || '')}
                                                                            selectionFormat={(option) => `${option.name}`}
                                                                            optionFormat='name'
                                                                            allowNoneOption={false}                           
                                                                        />
                                                                    </>
                                                                )}
                                                                <div className='delete-icon tooltip-icon'>
                                                                    <FontAwesomeIcon 
                                                                        icon={faTrash} 
                                                                        onClick={() => handleDeleteRow(row.id?.toString() || row.uuid || '')} />
                                                                    <span className="tooltip">{t('general.delete')}</span>
                                                                </div>
                                                                <div className={`order-icon tooltip-icon ${rows.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                    <FontAwesomeIcon icon={faGripLines} />
                                                                    <span className="tooltip">{t('general.reorder')}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                }}
                                            </Draggable>
                                        )
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    {errorMessages && 
                        <div className='error-message'>
                            {renderRowsFieldErrorMessages(errorMessages, 'items', t)}
                        </div>
                    }
                    <div onClick={(e) => {e.preventDefault(); handleAddRow(); }} 
                        className="add-new-button">
                        {t('job.items.add_new_article')}
                    </div>    
                    {helperText && 
                        <div className="helper-text">
                            {t(helperText)}
                        </div>
                    }
                </div>
            ) : (
                // View mode
                <div className={`full-width-alignment ${isEditable?.editability && !userHasOnlyViewRights ? 'editable' : ''}`}>
                    <div className="view-mode rows-view-mode">
                        <span className='p'>
                            <div className='item-list'>
                                {rows.some(row => row.id) && (
                                    // Show the column labels above the rows
                                    <div className={`rows-header view-row view-job-items-row ${showPriceFields ? 'show-price-fields' : ''} `}>
                                        <div className='header-item'>{t('job.items.quantity_label')}</div>
                                        <div className='header-item'>{t('job.items.description_label')}</div>
                                        <div className='header-item'>{t('job.items.purchase_price_label')}</div>
                                        {showPriceFields && <>
                                            <div className='header-item'>{t('job.items.price_label')}</div>
                                            <div className='header-item'>{t('job.items.total_label')}</div>
                                            <div className='header-item'>{t('job.items.vat_rate_label')}</div>
                                        </>}
                                    </div>
                                )}
                                {rows.filter(row => row.id).map((row) => {
                                    // Find the vat rate of the row to show its name
                                    const vatRate = vatRates.find(vatRate => vatRate.id === row.vat_rate);

                                    return (
                                        <div key={row.id!.toString()} className={`item view-row view-job-items-row ${showPriceFields ? 'show-price-fields' : ''} `}>
                                            <div>{row.quantity_registrated_string}</div>
                                            <div style={{ whiteSpace: "pre" }}>{row.name}{row.number ? ` (${row.number})` : ''}</div>
                                            <div>{formatPriceValue(row.purchase_price)}</div>
                                            {showPriceFields && <>
                                                <div>{formatPriceValue(row.sale_price)}</div>
                                                <div>{formatTotalPrice(row)}</div>
                                                <div>{vatRate ? vatRate.name : ''}</div>
                                            </>}
                                        </div>
                                    )
                                })}
                                {isEditable?.editability && !userHasOnlyViewRights && (
                                    // Only show add item label when the object is editable
                                    <div className='add-item'>
                                        {t('job.items.add_article')}
                                    </div>
                                )}
                            </div>
                        </span>
                        <span className='edit-icon'>
                            <FontAwesomeIcon icon={faPen} />
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default JobItemsFieldset;